// METER UNA VARIABLE DONDE ESTA EL CURSOR.
//
// La parte del hook `useVarInserter` que no necesita React, aparte para poder
// probarla: lo que se rompio aqui no se ve leyendo el codigo —cada linea parece
// razonable— y solo aparece al reproducir los gestos de una persona.
//
// ── LO QUE ROMPIA ──────────────────────────────────────────────────────────
//
// Crismar Perez (Maricarmen Nieto), 2026-09-18, sobre el mensaje de los Kits:
// *"al copiar y pegar o editar utilizando las celdas de programación, se están
// presentando algunas fallas. En algunos casos, se elimina contenido o se
// termina escribiendo sobre otras celdas"*. En su captura se veia
//
//     ...tu aplicación de {tipo de pó{compania}{compañía} ya está...
//
// o sea la ficha metida DENTRO de otra variable, partiendola, y texto perdido.
//
// LA CAUSA. El campo activo se guardaba SOLO al enfocar, y con el se guardaba
// `getValue`, que en React es un cierre sobre una CONSTANTE DE ESE RENDER
// (`const [body] = useState()`). O sea que `() => body` no lee el estado de
// ahora: devuelve el valor congelado de entonces. Al pulsar una ficha se cogia
// el texto de CUANDO SE HIZO FOCO y se le aplicaba el cursor del texto de AHORA:
//
//   · todo lo escrito o pegado desde el foco se perdia — el texto volvia atras;
//   · y la variable caia en una posicion que en el texto viejo significa otra
//     cosa, normalmente en medio de otra `{variable}`.
//
// Los tres gestos que reporto —teclear y pulsar ficha, dos fichas seguidas, y
// pegar y pulsar ficha— corrompian el texto los tres. Estan en
// `tests/unit/insercionDeVariables.test.ts`, y fallan con el codigo de antes.
//
// ── LAS DOS REGLAS QUE LO ARREGLAN ─────────────────────────────────────────
//
// 1. EL TEXTO SE LEE DEL CAMPO, no del cierre. `el.value` no puede quedarse
//    viejo, y es el MISMO sitio de donde sale el cursor: texto y posicion vienen
//    siempre del mismo instante, que es justo lo que se habia roto. `getValue()`
//    queda como respaldo para un campo que no exponga `value`.
//
// 2. EL CAMPO ACTIVO SE REFRESCA al escribir y al mover el cursor, no solo al
//    enfocar. Lo que se quedaba viejo no era solo el valor: tambien `setValue`.
//    En Tickets es `(v) => set(k, v)` sobre el objeto de su render, asi que con
//    el cierre viejo insertar una ficha revertia los OTROS campos del formulario.
//
// La leccion, para la proxima: un cierre guardado en un `ref` es una FOTO del
// render en que se guardo. Si lo que guarda tiene que seguir vivo, o se refresca
// con cada cambio, o se lee del DOM — pero no se guarda y ya.

/**
 * @param {{current: any}} ref donde se recuerda el campo activo. Se pasa de
 *   fuera para que este archivo no importe React y se pueda probar en Node.
 */
export function crearInsertador(ref) {
  /**
   * `insertAtCaret` es opcional y solo lo usan los editores con formato: un
   * contentEditable no tiene selectionStart, asi que sabe meter el texto en su
   * propio cursor y lo hace el.
   */
  const bind = (getValue, setValue, insertAtCaret) => {
    const recordar = (e) => { ref.current = { el: e.target, getValue, setValue, insertAtCaret }; };
    return {
      onFocus: recordar,
      // Escribir Y PEGAR pasan por aqui, que es la mitad del ticket.
      onInput: recordar,
      // Mover el cursor o seleccionar: no cambia el texto, pero si de que render
      // son los cierres guardados.
      onSelect: recordar,
    };
  };

  const insert = (tag) => {
    const cur = ref.current;
    if (!cur) return;
    // El editor con formato inserta en su propio cursor. Sin esto caeria en el
    // camino de abajo, que sin selectionStart pega la variable AL FINAL de todo
    // el HTML — detras de la ultima etiqueta, no donde esta el cursor.
    if (cur.insertAtCaret) { cur.insertAtCaret(tag); return; }
    const el = cur.el;
    if (!el) return;
    // Regla 1: el texto VIVO del campo, del mismo sitio que el cursor.
    const val = typeof el.value === 'string' ? el.value : (cur.getValue() || '');
    let start = val.length;
    let end = val.length;
    try {
      if (typeof el.selectionStart === 'number') { start = el.selectionStart; end = el.selectionEnd; }
    } catch { /* algunos tipos de input no exponen seleccion: se añade al final */ }
    // Un cursor que no cabe en el texto —lo unico que puede descuadrar ya— se
    // trata como "al final" en vez de partir nada por la mitad.
    if (!(start >= 0 && start <= val.length)) { start = val.length; end = val.length; }
    if (!(end >= start && end <= val.length)) end = start;
    cur.setValue(val.slice(0, start) + tag + val.slice(end));
    // El cursor vuelve justo detras de lo insertado, ya repintado por React.
    setTimeout(() => {
      try { el.focus(); const p = start + tag.length; el.selectionStart = el.selectionEnd = p; } catch { /* noop */ }
    }, 20);
  };

  return { bind, insert };
}
